/**
 * File Validation Utilities for OurNet Platform
 * Validates file uploads using magic bytes (file signatures) to prevent malicious uploads
 */

// Magic byte signatures for common image formats
const IMAGE_SIGNATURES = {
  // JPEG: FF D8 FF
  jpeg: {
    signature: [0xFF, 0xD8, 0xFF],
    mimeTypes: ['image/jpeg', 'image/jpg'],
    extensions: ['.jpg', '.jpeg']
  },
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  png: {
    signature: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A],
    mimeTypes: ['image/png'],
    extensions: ['.png']
  },
  // GIF: 47 49 46 38 (GIF8)
  gif: {
    signature: [0x47, 0x49, 0x46, 0x38],
    mimeTypes: ['image/gif'],
    extensions: ['.gif']
  },
  // WebP: 52 49 46 46 ... 57 45 42 50 (RIFF...WEBP)
  webp: {
    signature: [0x52, 0x49, 0x46, 0x46],
    secondarySignature: { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] },
    mimeTypes: ['image/webp'],
    extensions: ['.webp']
  },
  // BMP: 42 4D (BM)
  bmp: {
    signature: [0x42, 0x4D],
    mimeTypes: ['image/bmp'],
    extensions: ['.bmp']
  },
  // SVG: Check for XML/SVG content (text-based)
  svg: {
    signature: null, // Text-based, handled separately
    mimeTypes: ['image/svg+xml'],
    extensions: ['.svg'],
    textPattern: /<svg/i
  }
};

// Allowed MIME types for images
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp'
  // Note: SVG excluded by default due to XSS risk
];

// Maximum file sizes
const MAX_FILE_SIZES = {
  image: 5 * 1024 * 1024,      // 5MB
  thumbnail: 500 * 1024,       // 500KB
  avatar: 2 * 1024 * 1024      // 2MB
};

/**
 * Read the first N bytes of a file as Uint8Array
 * @param {File} file - File object
 * @param {number} numBytes - Number of bytes to read
 * @returns {Promise<Uint8Array>} First N bytes of the file
 */
const readFileBytes = (file, numBytes = 16) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const arr = new Uint8Array(reader.result);
      resolve(arr);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file.slice(0, numBytes));
  });
};

/**
 * Check if bytes match a signature
 * @param {Uint8Array} bytes - File bytes
 * @param {number[]} signature - Expected signature bytes
 * @param {number} offset - Offset to start checking (default 0)
 * @returns {boolean} True if signature matches
 */
const matchesSignature = (bytes, signature, offset = 0) => {
  if (!signature || !bytes || bytes.length < offset + signature.length) {
    return false;
  }
  return signature.every((byte, index) => bytes[offset + index] === byte);
};

/**
 * Validate file is a genuine image by checking magic bytes
 * @param {File} file - File object to validate
 * @returns {Promise<{valid: boolean, format: string|null, error: string|null}>}
 */
export const validateImageFile = async (file) => {
  // Check if file exists
  if (!file) {
    return { valid: false, format: null, error: 'No file provided' };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZES.image) {
    return {
      valid: false,
      format: null,
      error: `File size exceeds maximum of ${MAX_FILE_SIZES.image / (1024 * 1024)}MB`
    };
  }

  // Check MIME type first (quick check) — skip if empty, Android sometimes omits file.type
  if (file.type && !ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return {
      valid: false,
      format: null,
      error: `File type "${file.type}" is not allowed. Allowed types: JPEG, PNG, GIF, WebP, BMP`
    };
  }

  try {
    // Read magic bytes
    const bytes = await readFileBytes(file, 16);

    // Check each format's signature
    for (const [format, config] of Object.entries(IMAGE_SIGNATURES)) {
      if (!config.signature) continue; // Skip text-based formats like SVG

      if (matchesSignature(bytes, config.signature)) {
        // Check secondary signature if exists (e.g., WebP)
        if (config.secondarySignature) {
          const { offset, bytes: secondaryBytes } = config.secondarySignature;
          if (!matchesSignature(bytes, secondaryBytes, offset)) {
            continue;
          }
        }

        // Verify MIME type matches detected format (skip if empty — Android sometimes omits file.type)
        if (file.type && !config.mimeTypes.includes(file.type)) {
          return {
            valid: false,
            format,
            error: `File extension/type mismatch: file appears to be ${format.toUpperCase()} but was uploaded as ${file.type}`
          };
        }

        return { valid: true, format, error: null };
      }
    }

    // No valid signature found
    return {
      valid: false,
      format: null,
      error: 'File does not appear to be a valid image. File signature does not match any known image format.'
    };

  } catch (err) {
    return {
      valid: false,
      format: null,
      error: `File validation error: ${err.message}`
    };
  }
};

/**
 * Quick validation - checks MIME type and extension only (no magic bytes)
 * Use when you need fast validation and trust the source
 * @param {File} file - File to validate
 * @returns {{valid: boolean, error: string|null}}
 */
export const quickValidateImage = (file) => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  if (file.size > MAX_FILE_SIZES.image) {
    return { valid: false, error: 'File too large (max 5MB)' };
  }

  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'File is not an image' };
  }

  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return { valid: false, error: `File type ${file.type} is not allowed` };
  }

  return { valid: true, error: null };
};

/**
 * Get file extension from filename
 * @param {string} filename
 * @returns {string} Extension with dot (e.g., '.jpg')
 */
export const getFileExtension = (filename) => {
  if (!filename || typeof filename !== 'string') return '';
  const lastDot = filename.lastIndexOf('.');
  return lastDot === -1 ? '' : filename.slice(lastDot).toLowerCase();
};

/**
 * Check if file extension matches MIME type
 * @param {File} file
 * @returns {boolean}
 */
export const extensionMatchesMimeType = (file) => {
  const ext = getFileExtension(file.name);

  for (const config of Object.values(IMAGE_SIGNATURES)) {
    if (config.mimeTypes.includes(file.type)) {
      return config.extensions.includes(ext);
    }
  }

  return false;
};

export default {
  validateImageFile,
  quickValidateImage,
  getFileExtension,
  extensionMatchesMimeType,
  ALLOWED_IMAGE_TYPES,
  MAX_FILE_SIZES
};
